import { GabaritoAdsConfig, AdConfigGabaritoAds, AdTargetGabaritoAds, GabaritoAdsQuestionElement } from './interface';
import { cleanupPreviousGabaritoAds, displayGabaritoAd } from './display';
import { setupGabaritoRewardAdsListeners, displayGabaritoRewardAd, showGabaritoRewardAd } from './rewardAds';
import { handleEmptyAdFallback } from './fallback';

declare global {
    interface Window {
        sg_gabarito_ads_config: GabaritoAdsConfig;
        googletag: any;
        Sg_GPT_onTriggerCallback?: (config: AdConfigGabaritoAds, target: AdTargetGabaritoAds) => void;
        Sg_GPT_onTriggerRemoveCallback?: (element: HTMLElement, adUnits: string[]) => boolean;
    }

    namespace googletag {
        namespace events {
            interface SlotRenderEndedEvent {
                slot: {
                    getSlotElementId: () => string;
                };
                isEmpty: boolean;
                size: [number, number] | number;
            }
        }
    }
}

function initializeGabaritoAdsConfig(): void {
    const adFrequencyElement = document.querySelector('[data-gabarito-ads-frequency]');
    const adFrequency = adFrequencyElement ? 
        parseInt(adFrequencyElement.getAttribute('data-gabarito-ads-frequency') || '3', 10) : 3;

    window.sg_gabarito_ads_config = {
        GabaritoAdsQuestionElements: [],
        adUnitPathMob: '/1062898/TODAMATERIA_BR_MOB_GABARITO_MREC',
        adUnitPathDesk: '/1062898/TODAMATERIA_BR_DESK_GABARITO_MREC',
        adCounter: 0,
        fallbackLink: document.querySelector('[data-gabarito-ads-link]')?.getAttribute('data-gabarito-ads-link') || '',
        rewardAds: document.querySelector('[data-gabarito-reward-ads]')?.hasAttribute('data-gabarito-reward-ads') || false,
        adUnitPathMobReward: '/1062898/TODAMATERIA_BR_MOB_GABARITO_REWARDAD_MREC',
        // adUnitPathMobReward: '/22639388115/rewarded_web_example',
        // adUnitPathMobReward: '/22639388115/empty_rewarded_web_examples',
        rewardSlot: null,
        rewardSlotReady: false,
        retryEmptyRewardAd: 10,
        version: 3,
        adFrequency: adFrequency,
    } as GabaritoAdsConfig;

    let elementCounter = 0;

    document.querySelectorAll('.multiple-choice-wrapper').forEach(wrapper => {
        const validateButton = wrapper.querySelector('.multiple-choice--validate');
        const answerInfo = wrapper.querySelector('.answer-info');
        if (validateButton && answerInfo) {
            const elementId = wrapper.id || `sg-gabarito-el-${elementCounter++}`;
            wrapper.id = elementId;
            const dataId = (validateButton as HTMLElement).dataset.id || elementId;
            window.sg_gabarito_ads_config.GabaritoAdsQuestionElements.push({
                id: elementId,
                triggerSelector: '.multiple-choice--validate',
                answerSelector: '.answer-info',
                type: 'multiple-choice',
                dataId,
                hasShown: false,
                shouldShow: true,
            });
        }
    });

    document.querySelectorAll('.hidden-content').forEach(hiddenContent => {
        const label = hiddenContent.querySelector('.answer-labels');
        const checkbox = hiddenContent.querySelector('.answer-buttons');
        const answerContents = hiddenContent.querySelector('.answer-contents');
        if (label && checkbox && answerContents) {
            const elementId = hiddenContent.id || `sg-gabarito-el-${elementCounter++}`;
            hiddenContent.id = elementId;
            const checkboxId = checkbox.id || `sg-cb-${elementCounter}`;
            checkbox.id = checkboxId;
            window.sg_gabarito_ads_config.GabaritoAdsQuestionElements.push({
                id: elementId,
                triggerSelector: '.answer-labels',
                answerSelector: '.answer-contents',
                checkboxSelector: `#${checkboxId}`,
                type: 'hidden-content',
                hasShown: false,
                shouldShow: true
            });
        }
    });

    console.log(`[Gabarito Ads] Configuração inicializada v2`, window.sg_gabarito_ads_config);
}

export function handleGabaritoAdTrigger(questionConfig: GabaritoAdsQuestionElement): void {
    const LOG_PREFIX = "[Gabarito Ads]";

    if (questionConfig.hasShown) {
        const targetAnswerElement = document.querySelector(`#${questionConfig.id} ${questionConfig.answerSelector}`);
        if (targetAnswerElement) {
            (targetAnswerElement as HTMLElement).style.cssText = '';
            targetAnswerElement.classList.add('active');
        }
        if (questionConfig.type === 'hidden-content' && questionConfig.checkboxSelector) {
            const checkbox = document.querySelector(questionConfig.checkboxSelector) as HTMLInputElement;
            if (checkbox) {
                checkbox.checked = true;
                checkbox.disabled = false;
            }
        }
        return;
    }

    const isMobile = window.innerWidth <= 1024;
    if (!window.sg_gabarito_ads_config) {
        console.error(`${LOG_PREFIX} Configuração global sg_gabarito_ads_config em falta.`);
        return;
    }

    window.sg_gabarito_ads_config.adCounter = (window.sg_gabarito_ads_config.adCounter ?? 0) + 1;
    const adFrequency = window.sg_gabarito_ads_config.adFrequency || 3;
    questionConfig.shouldShow = (window.sg_gabarito_ads_config.adCounter % adFrequency === 0);
    console.log(`${LOG_PREFIX} Contador: ${window.sg_gabarito_ads_config.adCounter}. Frequência: ${adFrequency}. shouldShow para ${questionConfig.id}: ${questionConfig.shouldShow}`);

    if (!questionConfig.shouldShow) {
         console.log(`${LOG_PREFIX} Contador não é múltiplo de ${adFrequency}. Revelando resposta para ${questionConfig.id} sem anúncio.`);
         const targetAnswerElement = document.querySelector(`#${questionConfig.id} ${questionConfig.answerSelector}`);
         if (targetAnswerElement) {
            (targetAnswerElement as HTMLElement).style.cssText = '';
            targetAnswerElement.classList.add('active');
         }
         if (questionConfig.type === 'hidden-content' && questionConfig.checkboxSelector) {
            const checkbox = document.querySelector(questionConfig.checkboxSelector) as HTMLInputElement;
            if (checkbox) {
                checkbox.checked = true;
                checkbox.disabled = false;
            }
         }
         questionConfig.hasShown = true;
        return;
    }

    console.log(`${LOG_PREFIX} Tentando mostrar anúncio para ${questionConfig.id}.`);
    cleanupPreviousGabaritoAds(questionConfig.id, isMobile);

    setTimeout(() => {
        const questionWrapperElement = document.getElementById(questionConfig.id);
        const targetAnswerElement = questionWrapperElement?.querySelector(questionConfig.answerSelector);

        if (!questionWrapperElement || !targetAnswerElement) {
            return;
        }

        console.log(`${LOG_PREFIX} Elementos encontrados para ${questionConfig.id}. Verificando tipo de anúncio...`);
        if (window.sg_gabarito_ads_config.rewardAds && isMobile) {
            console.log(`${LOG_PREFIX} Mostrar Reward Ad para ${questionConfig.id}.`);
            const slot = window.sg_gabarito_ads_config.rewardSlot;
            showGabaritoRewardAd(questionConfig, slot);
        } else {
            console.log(`${LOG_PREFIX} Mostrar Ad Normal para ${questionConfig.id}.`);
            displayGabaritoAd(questionConfig, isMobile, targetAnswerElement as HTMLElement, questionWrapperElement);
            questionConfig.hasShown = true;
        }

        window.sg_gabarito_ads_config.adCurrentId = questionConfig.id;

    }, 100);
}

export default function initGabaritoAds(): void {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeGabaritoAdsConfig);
    } else {
        initializeGabaritoAdsConfig();
    }

    window.googletag.cmd.push(() => {

        if (window.sg_gabarito_ads_config?.rewardAds) {
            setupGabaritoRewardAdsListeners();
            displayGabaritoRewardAd();
        }

        document.body.addEventListener('click', (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            let wrapperElement: HTMLElement | null = null;
            let elementType: string | null = null;

            if (!window.sg_gabarito_ads_config?.GabaritoAdsQuestionElements) {
                initializeGabaritoAdsConfig();
                if (!window.sg_gabarito_ads_config?.GabaritoAdsQuestionElements) return;
            }

            const mcTrigger = target.closest('.multiple-choice--validate');
            const hcTrigger = target.closest('.answer-labels');

            if (mcTrigger?.closest('.multiple-choice-wrapper')) {
                wrapperElement = mcTrigger.closest('.multiple-choice-wrapper');
                elementType = 'multiple-choice';
            } else if (hcTrigger?.closest('.hidden-content')) {
                wrapperElement = hcTrigger.closest('.hidden-content');
                elementType = 'hidden-content';
            }

            if (wrapperElement?.id) {
                const elementConfig = window.sg_gabarito_ads_config.GabaritoAdsQuestionElements.find(el => el.id === wrapperElement?.id);
                if (elementConfig) {
                    if (elementType === 'hidden-content') {
                        const checkbox = document.querySelector(elementConfig.checkboxSelector || '') as HTMLInputElement;
                        if (checkbox && !checkbox.disabled) {
                            requestAnimationFrame(() => {
                                const targetAnswerElement = document.getElementById(elementConfig.id) as HTMLElement;
                                if (targetAnswerElement) {
                                    const headerHeight = 120;
                                    const elementRect = targetAnswerElement.getBoundingClientRect();
                                    const elementTopRelativeToDocument = elementRect.top + window.scrollY;
                                    const targetScrollY = elementTopRelativeToDocument - headerHeight;
                                    window.scrollTo({
                                        top: targetScrollY,
                                        behavior: 'smooth'
                                    });
                                }
                            });
                            setTimeout(() => {
                                if (checkbox.checked) {
                                    handleGabaritoAdTrigger(elementConfig);
                                }
                            }, 50);
                        }
                    } else if (elementType === 'multiple-choice') {
                        handleGabaritoAdTrigger(elementConfig);
                    }
                }
            }
        });

        window.googletag.pubads().addEventListener('slotRenderEnded', (event: googletag.events.SlotRenderEndedEvent) => {
            const slotElementId = event.slot.getSlotElementId();
            const slotElement = document.getElementById(slotElementId);
            const isNormalGabaritoSlot = slotElement?.closest('.ad-gabarito__overlay, .hidden-content-ad__overlay') !== null;

            if (event.isEmpty && isNormalGabaritoSlot) {
                const adContainer = document.getElementById(slotElementId);
                let overlayElement: HTMLElement | null = null;
                let config: GabaritoAdsQuestionElement | undefined;

                overlayElement = adContainer?.closest('.gabarito-ads__overlay, .hidden-content-ad__overlay') as HTMLElement;
                const configIdPart = slotElementId.replace(/^ad-(mc|hc)-/, '');
                config = window.sg_gabarito_ads_config?.GabaritoAdsQuestionElements.find(el => el.dataId === configIdPart || el.checkboxSelector?.substring(1) === configIdPart);

                if (adContainer && overlayElement) {
                    handleEmptyAdFallback(slotElementId, adContainer, overlayElement, config);
                }
            }
        });
    });
}